import { Box, DialogContent, DialogTitle } from "@mui/material";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { UpdateMaterialRequest } from "api/models/request/product/material";
import { roundTo } from "commons/ultilities";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import { observer } from "mobx-react-lite";
import { MaterialFixDecimalConstants } from "mobx-stores/_models/products/const";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getMaterialFormSchema } from "./schema";

export const UpdateMaterial = observer(() => {
  const {
    productManagement: {
      openUpdateMaterial,
      setProp,
      productDetail,
      getProductDetail,
    },
  } = useStores();
  const {
    form: { controls, isValid },
    submitHandler,
    getControl,
  } = useForm<UpdateMaterialRequest>(getMaterialFormSchema(), {
    gsmLower: productDetail?.material?.gsmLower,
    gsmStandard: productDetail?.material?.gsmStandard,
    gsmUpper: productDetail?.material?.gsmUpper,
    paperBrand: productDetail?.material?.paperBrand || "",
    paperType: productDetail?.material?.paperType,
    standard: productDetail?.material?.standard || "",
    thicknessLower: productDetail?.material?.thicknessLower,
    thicknessStandard: productDetail?.material?.thicknessStandard,
    thicknessUpper: productDetail?.material?.thicknessUpper,
    whitenessLower: productDetail?.material?.whitenessLower,
    whitenessStandard: productDetail?.material?.whitenessStandard,
    whitenessUpper: productDetail?.material?.whitenessUpper,
    gsmLowerFactorPercent: productDetail?.material?.gsmLowerFactorPercent,
    gsmUpperFactorPercent: productDetail?.material?.gsmUpperFactorPercent,
    thicknessLowerFactorPercent:
      productDetail?.material?.thicknessLowerFactorPercent,
    thicknessUpperFactorPercent:
      productDetail?.material?.thicknessUpperFactorPercent,
    whitenessLowerFactorPercent:
      productDetail?.material?.whitenessLowerFactorPercent,
    whitenessUpperFactorPercent:
      productDetail?.material?.whitenessUpperFactorPercent,
  });

  const saveHandler = useCallback(
    async (data: UpdateMaterialRequest) => {
      if (!productDetail || !productDetail?.id) return;
      await productBussiness.updateMaterial(productDetail?.id, data);
      await getProductDetail(productDetail?.id);
      setProp("openUpdateMaterial", false);
    },
    [getProductDetail, productDetail, setProp]
  );

  const calcUpperGsm = useCallback(() => {
    const gsmStandard = getControl("gsmStandard").value as number | null;
    const gsmUpperFactorPercent = getControl("gsmUpperFactorPercent").value as
      | number
      | null;
    if (gsmStandard != null && gsmUpperFactorPercent != null) {
      const gsmUpper =
        gsmStandard + (gsmStandard * gsmUpperFactorPercent) / 100;
      getControl("gsmUpper").setValueAndValidate(
        roundTo(gsmUpper, MaterialFixDecimalConstants.GSM_UPPER)
      );
    }
  }, [getControl]);

  const calcLowerGsm = useCallback(() => {
    const gsmStandard = getControl("gsmStandard").value as number | null;
    const gsmLowerFactorPercent = getControl("gsmLowerFactorPercent").value as
      | number
      | null;
    if (gsmStandard != null && gsmLowerFactorPercent != null) {
      const gsmLower =
        gsmStandard - (gsmStandard * gsmLowerFactorPercent) / 100;
      getControl("gsmLower").setValueAndValidate(
        roundTo(gsmLower, MaterialFixDecimalConstants.GSM_LOWER)
      );
    }
  }, [getControl]);

  const calcUpperThickness = useCallback(() => {
    const thicknessStandard = getControl("thicknessStandard").value as
      | number
      | null;
    const thicknessUpperFactorPercent = getControl(
      "thicknessUpperFactorPercent"
    ).value as number | null;
    if (thicknessStandard != null && thicknessUpperFactorPercent != null) {
      const thicknessUpper =
        thicknessStandard +
        (thicknessStandard * thicknessUpperFactorPercent) / 100;

      getControl("thicknessUpper").setValueAndValidate(
        roundTo(thicknessUpper, MaterialFixDecimalConstants.THICKNESS_UPPER)
      );
    }
  }, [getControl]);

  const calcLowerThickness = useCallback(() => {
    const thicknessStandard = getControl("thicknessStandard").value as
      | number
      | null;
    const thicknessLowerFactorPercent = getControl(
      "thicknessLowerFactorPercent"
    ).value as number | null;
    if (thicknessStandard != null && thicknessLowerFactorPercent != null) {
      const thicknessLower =
        thicknessStandard -
        (thicknessStandard * thicknessLowerFactorPercent) / 100;

      getControl("thicknessLower").setValueAndValidate(
        roundTo(thicknessLower, MaterialFixDecimalConstants.THICKNESS_LOWER)
      );
    }
  }, [getControl]);

  const calcUpperWhiteness = useCallback(() => {
    const whitenessStandard = getControl("whitenessStandard").value as
      | number
      | null;
    const whitenessUpperFactorPercent = getControl(
      "whitenessUpperFactorPercent"
    ).value as number | null;
    if (whitenessStandard != null && whitenessUpperFactorPercent != null) {
      const whitenessUpper =
        whitenessStandard +
        (whitenessStandard * whitenessUpperFactorPercent) / 100;

      getControl("whitenessUpper").setValueAndValidate(
        roundTo(whitenessUpper, MaterialFixDecimalConstants.WHITENESS_UPPER)
      );
    }
  }, [getControl]);

  const calcLowerWhiteness = useCallback(() => {
    const whitenessStandard = getControl("whitenessStandard").value as
      | number
      | null;
    const whitenessLowerFactorPercent = getControl(
      "whitenessLowerFactorPercent"
    ).value as number | null;
    if (whitenessStandard != null && whitenessLowerFactorPercent != null) {
      const whitenessLower =
        whitenessStandard -
        (whitenessStandard * whitenessLowerFactorPercent) / 100;
      console.log("Lower Whiteness:", whitenessLower);
      getControl("whitenessLower").setValueAndValidate(
        roundTo(whitenessLower, MaterialFixDecimalConstants.WHITENESS_LOWER)
      );
    }
  }, [getControl]);

  const handleValueChange = useCallback(
    (control: IControl) => {
      //gsm
      if (control.name === "gsmStandard") {
        // Calc upper gsm and lower gsm
        calcUpperGsm();
        calcLowerGsm();
      }

      if (control.name === "gsmUpperFactorPercent") {
        // Calc upper gsm
        calcUpperGsm();
      }

      if (control.name === "gsmLowerFactorPercent") {
        // Calc lower gsm
        calcLowerGsm();
      }
      //end

      //thickness

      if (control.name === "thicknessStandard") {
        // Calc upper thickness and lower thickness
        calcUpperThickness();
        calcLowerThickness();
      }

      if (control.name === "thicknessUpperFactorPercent") {
        // Calc upper thickness
        calcUpperThickness();
      }

      if (control.name === "thicknessLowerFactorPercent") {
        // Calc lower thickness
        calcLowerThickness();
      }
      //end

      //whiteness
      if (control.name === "whitenessStandard") {
        // Calc upper whiteness and lower whiteness
        calcUpperWhiteness();
        calcLowerWhiteness();
      }

      if (control.name === "whitenessUpperFactorPercent") {
        // Calc upper whiteness
        calcUpperWhiteness();
      }

      if (control.name === "whitenessLowerFactorPercent") {
        // Calc lower whiteness
        calcLowerWhiteness();
      }
      //end
    },
    [
      calcLowerGsm,
      calcLowerThickness,
      calcLowerWhiteness,
      calcUpperGsm,
      calcUpperThickness,
      calcUpperWhiteness,
    ]
  );

  return (
    <DialogForm
      open={openUpdateMaterial}
      fullWidth
      maxWidth={"md"}
      onClose={() => setProp("openUpdateMaterial", false)}
      onCancelBtnClicked={() => setProp("openUpdateMaterial", false)}
      onSaveBtnClick={async () => {
        await submitHandler(saveHandler);
      }}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>
        {`Cập nhật vật tư: `}{" "}
        <span style={{ color: "blue" }}>{`${productDetail?.name}${
          productDetail?.customer?.dealingName
            ? `- ${productDetail?.customer?.dealingName}`
            : ""
        }`}</span>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: "10px" }}>
          <DynamicForm
            controls={controls}
            onValueChanged={handleValueChange}
          ></DynamicForm>
        </Box>
      </DialogContent>
    </DialogForm>
  );
});
